import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import classNames from 'classnames';
import { Badge } from 'primereact/badge';
import { MenuItem } from 'primereact/menuitem';
import { TabMenu } from 'primereact/tabmenu';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useLearner } from '../context';

const StyledTabMenu = styled(TabMenu)`
  &.p-tabmenu {
    padding-block: var(--default-padding);
    margin: 0px;
  }
`;

const StyledBadge = styled(Badge)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 23px;
  height: 23px;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 600;
  border-radius: 50%;
  background-color: var(--red-main);
  top: -12px;
  right: -6px;
`;

export const LearnerDashboardTabs: React.FC = () => {
  const { contentToDo } = useLearner();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleKeyDown = (event: React.KeyboardEvent, url: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(url);
    }
  };

  const getItemTemplate = (url: string, current: string) => (item: MenuItem) =>
    (
      <Link
        to={url}
        className={classNames({
          'p-menuitem-link': true,
          'p-menuitem-link-active': current.includes(url),
        })}
        role="menuitem"
        tabIndex={0}
        onKeyDown={(e) => handleKeyDown(e, url)}
      >
        <div
          className={
            item.data.toDo
              ? 'p-menuitem-text p-overlay-badge'
              : 'p-menuitem-text'
          }
          aria-label={
            item.data.toDo
              ? item.data.toDo > 1
                ? t('generic.toDo.num.items', { num: item.data.toDo })
                : t('generic.toDo.one.item')
              : undefined
          }
        >
          {item.label}
          {!!item.data.toDo && (
            <StyledBadge
              value={item.data.toDo}
              severity="danger"
              aria-hidden="true"
            />
          )}
        </div>
      </Link>
    );

  const tabItems: MenuItem[] = [
    {
      label: t('generic.toDo'),
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE](),
        location.pathname,
      ),
      data: {
        toDo: contentToDo,
        url: RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE](),
      },
    },
    {
      label: t('generic.completed'),
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_COMPLETED](),
        location.pathname,
      ),
      data: RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_COMPLETED](),
    },
  ];

  const paths = tabItems.map((item) => item.data?.url || item.data);
  const currentPath = location.pathname;
  const [activeIndex, setActiveIndex] = useState<number>(
    paths.indexOf(currentPath),
  );

  return (
    <StyledTabMenu
      model={tabItems}
      activeIndex={activeIndex}
      onTabChange={(e) => setActiveIndex(e.index)}
    />
  );
};
